import { useCallback, useEffect, useState } from 'react'

import {
  Sheet,
  SheetCloseButton,
  SheetContent,
  SheetOverlay,
  SheetTitle,
} from '@wartek-id/sheet-v1'
import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon'
import { Chips } from '@wartek-id/chips'

import { useSETrackerEvent } from 'app/InfoTerkiniPage/hooks/useSETrackEvent'
import styles from './FilterSheet.module.css'

import type { IInfoterkiniCategory } from 'types/info-terkini'

type IInfoterkiniFilterCategory = IInfoterkiniCategory & {
  selected?: boolean
}

export interface IFilterSheetProps {
  categories: IInfoterkiniCategory[]
  selectedCategoryIds?: string[]
  isSheetOpen?: boolean
  onFilter: (selectedCategoryIds: string[]) => void
  onSheetStateChange?: (boolean) => void
}

export default function FilterSheet(props: IFilterSheetProps) {
  const [showFilterSheet, setShowFilterSheet] = useState(false)

  const [categories, setCategories] = useState<IInfoterkiniFilterCategory[]>([])
  const [isAnyCategorySelected, setAnyCategorySelected] = useState(false)
  const [disableResetButton, setDisableResetButton] = useState(true)

  const tracker = useSETrackerEvent()

  const resetInternalStates = useCallback(() => {
    setCategories(
      props.categories.map((category) => ({
        ...category,
        selected: props.selectedCategoryIds?.includes(category.id) ?? false,
      }))
    )

    setAnyCategorySelected(props.selectedCategoryIds?.length > 0)
    setDisableResetButton(
      !props.selectedCategoryIds || props.selectedCategoryIds?.length === 0
    )
  }, [props.categories, props.selectedCategoryIds])

  useEffect(() => {
    resetInternalStates()
  }, [resetInternalStates])

  useEffect(() => {
    if (typeof props.isSheetOpen !== 'undefined') {
      setShowFilterSheet(props.isSheetOpen)
    }
  }, [props.isSheetOpen])

  const toggleSelected = (categoryId) => {
    const category = categories.find((category) => category.id === categoryId)
    category.selected = !category.selected
    setCategories([...categories])
    setDisableResetButton(categories.every((category) => !category.selected))
  }

  const onFilterClick = () => {
    const selectedCategoryIds = categories
      .filter((category) => category.selected)
      .map((category) => category.id)

    setAnyCategorySelected(selectedCategoryIds.length > 0)

    // track selected categories without selected props
    tracker.SETrackInfoTerkiniChipClicked(
      categories
        .filter((category) => category.selected)
        .map((category) => ({
          id: category.id,
          name: category.name,
        }))
    )

    props.onFilter(selectedCategoryIds)

    setShowFilterSheet(false)
    props.onSheetStateChange?.(false)
  }

  const onResetClick = () => {
    setCategories(
      props.categories.map((category) => ({
        ...category,
        selected: false,
      }))
    )

    setDisableResetButton(true)
  }

  const closeSheet = () => {
    setShowFilterSheet(false)
    props.onSheetStateChange?.(false)

    resetInternalStates()
  }

  return (
    <div className="-mt-4 mb-4">
      <Button
        color="white"
        fullWidth
        size="sm"
        onClick={() => {
          props.onSheetStateChange?.(true)
          tracker.SETrackInfoTerkiniFilterButtonClicked()
          setShowFilterSheet(true)
        }}
        data-testid="filter-button"
      >
        <div className="flex items-center">
          <Icon className="mr-2">filter_alt</Icon>Filter
          {
            // show the badge if any of the categories is selected
            isAnyCategorySelected && (
              <Icon className="ml-2" color="informational">
                check_circle
              </Icon>
            )
          }
        </div>
      </Button>
      <Sheet
        closeOnOverlayClick
        onClose={() => closeSheet()}
        position="bottom"
        sheetSize="auto"
        type="modal"
        isOpen={showFilterSheet}
      >
        <SheetOverlay />
        <SheetContent data-testid="filter-sheet" className="max-w-xl m-auto">
          <SheetTitle>
            <span className="font-bold">Pilih kategori</span>
            <SheetCloseButton />
          </SheetTitle>

          <div className="px-6 py-4">
            <div className="flex flex-wrap gap-1">
              {categories.map((category) => (
                <Chips
                  size="sm"
                  key={category.id}
                  isActive={!!category.selected}
                  role="checkbox"
                  aria-checked={!!category.selected}
                  onClick={() => toggleSelected(category.id)}
                  className={styles.chip}
                >
                  {category.name}
                </Chips>
              ))}
            </div>
            <div className="mt-4">
              <Button color="blue" fullWidth onClick={onFilterClick}>
                Lihat Info
              </Button>
              <Button
                color="white"
                fullWidth
                className="mt-2"
                onClick={onResetClick}
                disabled={disableResetButton}
              >
                Reset Filter
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}
